import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Home,
  Users,
  Calculator,
  Package,
  CheckSquare,
  Calendar,
  Clock,
  CalendarDays,
  Box,
  Menu,
  Search,
  Bell,
  Settings,
  LogOut,
  Shield,
  Moon,
  Sun,
  BarChart3,
  BookUser,
  Layers,
  ExternalLink,
  FileSpreadsheet,
  Filter,
  Image as ImageIcon
} from 'lucide-react';
import { settingsAPI, externalLinksAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// Map icon string names to lucide-react components for dynamic external links
const ICON_MAP = {
  Filter,
  ExternalLink,
  Home,
  Users,
  Calculator,
  Package,
  CheckSquare,
  Calendar,
  Clock,
  CalendarDays,
  Box,
  BarChart3,
  BookUser,
  Layers,
  FileSpreadsheet,
  Image: ImageIcon,
};

const navigationItems = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Attendance', path: '/attendance', icon: Users, moduleKey: 'attendance' },
  { name: 'Address Book', path: '/address-book', icon: BookUser, moduleKey: 'address-book' },
  { name: 'CAD', path: '/cad-orders', icon: Layers, moduleKey: 'cad' },
  { name: 'Gold Calculator', path: '/gold-calculator', icon: Calculator, moduleKey: 'gold-calculator' },
  { name: 'Loss Cal', path: '/loss-cal', icon: Calculator, moduleKey: 'loss-cal' },
  { name: 'Estimate', path: '/estimate', icon: FileSpreadsheet, moduleKey: 'estimate' },
  { name: 'Defaults', path: '/defaults', icon: ImageIcon, moduleKey: 'defaults' },
  { name: 'Remainder', path: '/remainder', icon: Clock, moduleKey: 'remainder' },
  { name: 'Monthly Checklist', path: '/monthly-checklist', icon: Calendar, moduleKey: 'monthly-checklist' },
  { name: 'Weekly Checklist', path: '/weekly-checklist', icon: CalendarDays, moduleKey: 'weekly-checklist' },
  { name: 'Stocks/Indent', path: '/stocks-indent', icon: Box, moduleKey: 'stocks-indent' },
  { name: 'Reports', path: '/reports', icon: BarChart3, adminOnly: true, moduleKey: 'reports' },
];

export const Layout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModuleAccess } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [customLink, setCustomLink] = useState({ label: '', url: '' });
  const [externalLinks, setExternalLinks] = useState([]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsAPI.get();
        if (settings.custom_link_label && settings.custom_link_url) {
          setCustomLink({
            label: settings.custom_link_label,
            url: settings.custom_link_url
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    const loadExternalLinks = async () => {
      try {
        const links = await externalLinksAPI.list();
        setExternalLinks(Array.isArray(links) ? links.filter(l => l.enabled) : []);
      } catch (error) {
        console.error('Error loading external links:', error);
      }
    };
    loadSettings();
    loadExternalLinks();

    // Real-time sync: refresh when admin updates links in another tab (same browser)
    const handleStorageEvent = (e) => {
      if (e.key === 'external_links_updated_at') {
        loadExternalLinks();
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    // Real-time sync: refresh when admin updates links in the same tab
    const handleCustomEvent = () => loadExternalLinks();
    window.addEventListener('external-links-updated', handleCustomEvent);

    // Fallback polling every 30s for other browsers / devices
    const pollInterval = setInterval(loadExternalLinks, 30000);

    // Refresh when tab regains focus (cheaper than constant polling)
    const handleFocus = () => loadExternalLinks();
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('external-links-updated', handleCustomEvent);
      window.removeEventListener('focus', handleFocus);
      clearInterval(pollInterval);
    };
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleNavigation = (item) => {
    if (item.external) {
      window.open(item.external, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      return;
    }
    navigate(item.path);
    setIsMobileMenuOpen(false);
  };

  const getUserInitials = (username) => {
    return username ? username.substring(0, 2).toUpperCase() : 'JK';
  };

  const filteredNavItems = navigationItems.filter(item => {
    // Check search query match
    if (!item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Check admin-only items
    if (item.adminOnly && user?.role !== 'admin') {
      return false;
    }
    
    // Home is always accessible
    if (item.path === '/') {
      return true;
    }
    
    // Use explicit moduleKey or extract from path
    const moduleKey = item.moduleKey || (item.path ? item.path.substring(1) : '');
    
    // Check if user has access to this module
    return hasModuleAccess(moduleKey);
  });

  // Build dynamic external link nav items (admin-configurable, all users have access)
  const externalNavItems = externalLinks
    .filter(link => link.label.toLowerCase().includes(searchQuery.toLowerCase()))
    .map(link => ({
      name: link.label,
      external: link.url,
      icon: ICON_MAP[link.icon] || Filter,
      id: link.id,
    }));

  // Merge: place external links right after Home
  const homeItem = filteredNavItems.find(i => i.path === '/');
  const restItems = filteredNavItems.filter(i => i.path !== '/');
  const displayNavItems = [
    ...(homeItem ? [homeItem] : []),
    ...externalNavItems,
    ...restItems,
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar for desktop */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-sidebar-background text-sidebar-foreground transition-transform duration-300 hidden lg:block">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex items-center justify-center h-20 border-b border-sidebar-hover px-6">
            <h1 className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">Jewellers KSP</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3 scrollbar-hide">
            {displayNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = !item.external && location.pathname === item.path;
              return (
                <button
                  key={item.path || item.id || item.name}
                  onClick={() => handleNavigation(item)}
                  data-testid={item.external ? `sidebar-external-${item.name.toLowerCase().replace(/\s+/g, '-')}` : undefined}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-lg
                    transition-all duration-200 group
                    ${
                      isActive
                        ? 'bg-sidebar-accent text-primary-foreground shadow-emerald'
                        : 'text-sidebar-foreground hover:bg-sidebar-hover'
                    }
                  `}
                >
                  <Icon className={`h-5 w-5 ${
                    isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-sidebar-foreground'
                  }`} />
                  <span className="text-sm font-medium">{item.name}</span>
                  {item.external && (
                    <ExternalLink className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Admin Panel Link */}
          {user?.role === 'admin' && (
            <div className="border-t border-sidebar-hover px-3 py-4">
              <button
                onClick={() => handleNavigation({ path: '/admin' })}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-all duration-200
                  ${
                    location.pathname === '/admin'
                      ? 'bg-sidebar-accent text-primary-foreground shadow-emerald'
                      : 'text-sidebar-foreground hover:bg-sidebar-hover'
                  }
                `}
              >
                <Shield className="h-5 w-5" />
                <span className="text-sm font-medium">Admin Panel</span>
              </button>
            </div>
          )}

          {/* Custom Website Link */}
          {customLink.label && customLink.url && (
            <div className="border-t border-sidebar-hover px-3 py-4">
              <a
                href={customLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sidebar-foreground hover:bg-sidebar-hover transition-all duration-200"
              >
                <ExternalLink className="h-5 w-5" />
                <span className="text-sm font-medium">{customLink.label}</span>
              </a>
            </div>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-20 border-b bg-card backdrop-blur supports-[backdrop-filter]:bg-card/95">
          <div className="flex h-full items-center justify-between px-4 md:px-6">
            {/* Mobile menu and logo */}
            <div className="flex items-center gap-4">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 bg-sidebar-background">
                  <div className="flex h-full flex-col">
                    <div className="flex items-center justify-center h-20 border-b border-sidebar-hover">
                      <h1 className="text-xl font-bold bg-gradient-primary bg-clip-text text-transparent">Jewellers KSP</h1>
                    </div>
                    <nav className="flex-1 overflow-y-auto py-6 px-3 scrollbar-hide">
                      {displayNavItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = !item.external && location.pathname === item.path;
                        return (
                          <button
                            key={item.path || item.id || item.name}
                            onClick={() => handleNavigation(item)}
                            data-testid={item.external ? `sidebar-external-${item.name.toLowerCase().replace(/\s+/g, '-')}-mobile` : undefined}
                            className={`
                              w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-lg
                              transition-all duration-200
                              ${
                                isActive
                                  ? 'bg-sidebar-accent text-primary-foreground'
                                  : 'text-sidebar-foreground hover:bg-sidebar-hover'
                              }
                            `}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="text-sm font-medium">{item.name}</span>
                            {item.external && (
                              <ExternalLink className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                            )}
                          </button>
                        );
                      })}
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => handleNavigation({ path: '/admin' })}
                          className={`
                            w-full flex items-center gap-3 px-4 py-3 rounded-lg mt-4
                            transition-all duration-200
                            ${
                              location.pathname === '/admin'
                                ? 'bg-sidebar-accent text-primary-foreground'
                                : 'text-sidebar-foreground hover:bg-sidebar-hover'
                            }
                          `}
                        >
                          <Shield className="h-5 w-5" />
                          <span className="text-sm font-medium">Admin Panel</span>
                        </button>
                      )}
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Logo for mobile */}
              <h1 className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent lg:hidden">
                Jewellers KSP
              </h1>

              {/* Search bar */}
              <div className="hidden md:flex items-center relative w-72">
                <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Dark mode toggle */}
              <Button variant="ghost" size="icon" onClick={toggleDarkMode}>
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive"></span>
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getUserInitials(user?.username)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm font-medium">{user?.username}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.username}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-5rem)] p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;